import { useState } from 'preact/hooks';
import useFormStore from '../store/useFormStore';

/**
 * Format cart items for API submission
 * @param {Array} items - Cart items from zustand store
 * @returns {Array} Formatted line items
 */
const formatLineItems = (items) => {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  return items
    .filter(item => {
      // Exclude special items like service_charge and portable_equipment
      return item.item_id !== 'service_charge' && item.item_id !== 'portable_equipment';
    })
    .map(item => {
      const lineItem = {
        cost_per_unit: item.price_per_unit,
        item: item.label,
        item_total: item.price_per_unit * item.quantity,
        qty: item.quantity,
        service: parseInt(item.category_id, 10)
      };

      // Add addons if they exist
      if (item.addons && item.addons.length > 0) {
        lineItem.addons = item.addons.map(addon => ({
          cost_per_unit: addon.addon_price,
          qty: addon.quantity,
          item: addon.addon_name,
          item_total: addon.addon_price * addon.quantity,
          service: parseInt(item.category_id, 10)
        }));
      }

      return lineItem;
    });
};

/**
 * Format scheduled appointments for API submission
 * @param {Array} appointments - Scheduled appointments from zustand store
 * @returns {Array} Formatted appointments
 */
const formatAppointments = (appointments) => {
  if (!appointments || !Array.isArray(appointments)) {
    return [];
  }

  return appointments.map(appointment => {
    const formatted = {
      services: appointment.serviceIds ? appointment.serviceIds.map(id => parseInt(id, 10)) : [],
      date: appointment.timeslot?.date || null,
      timeslot: appointment.timeslot?.timeslot || null
    };

    // Only include extra_info if it exists (API expects array or omitted)
    if (appointment.timeslot?.extra) {
      formatted.extra_info = appointment.timeslot.extra;
    }

    return formatted;
  });
};

/**
 * Format discounts for API submission
 * @param {Array} discounts - Discounts from cart
 * @param {Object} currentFranchise - Current franchise data
 * @returns {Array} Formatted discounts
 */
const formatDiscounts = (discounts, currentFranchise) => {
  if (!discounts || discounts.length === 0) {
    return [];
  }

  return discounts.map(discount => {
    // Find the full promotion data from the franchise
    const promoData = currentFranchise?.promotions?.find(
      p => (p.promo_code === discount.code || p.promo_code === discount.promo_code)
    );

    const services = discount.services || promoData?.service || [];

    const formatted = {
      promo_code: discount.code || discount.promo_code,
      promo_title: promoData?.title || discount.title || '',
      total: discount.discount_amount || discount.amount || 0
    };

    // Only include services if it has values (API rejects empty arrays as null)
    if (services && services.length > 0) {
      formatted.services = services;
    }

    return formatted;
  });
};

/**
 * Build the complete order payload from zustand store data
 * @param {Object} params - Parameters object
 * @returns {Object} Complete order payload
 */
const buildOrderPayload = ({
  cart,
  scheduledAppointments,
  userFormInformation,
  selectedEstimateType,
  needsPortableEquipment,
  currentFranchise,
  shortLeadFormPreferredDate,
  shortLeadFormNote,
  selectedServices
}) => {
  // Short lead form has a different payload structure
  if (selectedEstimateType === 'on_site_estimate_short') {
    const payload = {
      request_type: selectedEstimateType,
      marketing_consent: userFormInformation.signUpForEmails || false,
      text_consent: userFormInformation.signUpForTexts || false,
      phone_number: userFormInformation.phoneNumber,
      email: userFormInformation.email,
      address: {
        given_name: userFormInformation.firstName,
        family_name: userFormInformation.lastName,
        address_line1: userFormInformation.streetAddress,
        address_line2: userFormInformation.unit || '',
        locality: userFormInformation.city,
        administrative_area: userFormInformation.state,
        postal_code: userFormInformation.postalCode,
        country_code: currentFranchise?.address?.country_code || 'US'
      },
      services: selectedServices.map(id => String(id))
    };

    // Only include preferred_date if it exists
    if (shortLeadFormPreferredDate) {
      payload.preferred_date = shortLeadFormPreferredDate;
    }

    // Only include note if it exists
    if (shortLeadFormNote) {
      payload.note = shortLeadFormNote;
    }

    return payload;
  }

  // Standard payload structure for normal flow
  const serviceChargeItem = cart.items?.find(item => item.item_id === 'service_charge');
  const additionalCharge = serviceChargeItem ? serviceChargeItem.price_per_unit : 0;
  const usesPricing = currentFranchise?.uses_pricing !== false;
  const discountsToSubmit = usesPricing ? cart.appliedDiscounts : cart.discounts;

  // cart.subtotal includes the service charge; back it out so subtotal + additional_charge + taxes == total.
  const subtotalExcludingServiceCharge = parseFloat(cart.subtotal || 0) - additionalCharge;

  const payload = {
    request_type: selectedEstimateType,
    marketing_consent: userFormInformation.signUpForEmails || false,
    text_consent: userFormInformation.signUpForTexts || false,
    phone_number: userFormInformation.phoneNumber,
    email: userFormInformation.email,
    needs_portable: needsPortableEquipment,
    address: {
      given_name: userFormInformation.firstName,
      family_name: userFormInformation.lastName,
      address_line1: userFormInformation.streetAddress,
      address_line2: userFormInformation.unit || '',
      locality: userFormInformation.city,
      administrative_area: userFormInformation.state,
      postal_code: userFormInformation.postalCode,
      country_code: currentFranchise?.address?.country_code || 'US'
    },
    discounts: formatDiscounts(discountsToSubmit, currentFranchise),
    line_items: formatLineItems(cart.items),
    appointments: formatAppointments(scheduledAppointments),
    additional_charge: additionalCharge,
    subtotal: subtotalExcludingServiceCharge,
    taxes: cart.taxes,
    total: cart.total
  };

  // Only include ad tracking fields if they have values
  // (Prevents "This value should not be null" validation errors)
  // if (adPlatform) payload.ad_platform = adPlatform;
  // if (adId) payload.ad_id = adId;
  // if (referringUrl) payload.referring_url = referringUrl;

  return payload;
};

/**
 * Extract error message from API response
 * @param {Object} result - API response result
 * @returns {string} Error message
 */
const extractErrorMessage = (result) => {
  if (!result) {
    return 'An unknown error occurred';
  }

  // Handle string message
  if (typeof result.message === 'string') {
    return result.message;
  }

  // Handle array of messages
  if (Array.isArray(result.message)) {
    return result.message.join(', ');
  }

  // Handle object message (validation errors)
  if (typeof result.message === 'object' && result.message !== null) {
    const errors = Object.values(result.message);
    return errors.join(', ');
  }

  // Fallback
  return 'Failed to submit order';
};

/**
 * Submit order to the API
 * @param {string} franchiseId - Franchise ID
 * @param {Object} payload - Order payload
 * @returns {Promise<Object>} API response
 * @throws {Error} If submission fails
 */
const submitOrder = async (franchiseId, payload) => {

  try {
    // Debug: Log payload
    // console.log('Submitting order with payload:', JSON.stringify(payload, null, 2));

    // Fetch CSRF token
    const csrfResponse = await fetch('/session/token');
    const csrfToken = await csrfResponse.text();

    // Get any extra data needed for submission (e.g. ad tracking).
    // Found in web/modules/custom/coit_rest/lib/index.jsx.
    if (typeof window !== 'undefined' && typeof window.getExtraData === 'function') {
      payload = {...payload, ...window.getExtraData()};
    }

    // Submit to API
    const response = await fetch(`/api/location/${franchiseId}/schedule`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(payload)
    });

    // Parse response
    let result;
    try {
      result = await response.json();
      // Test the response back
      // console.log('API response:', result);
    } catch (parseError) {
      throw new Error('Failed to parse server response');
    }

    // Debug: Log response
    // console.log('API Response:', result);

    // Check for API errors
    if (!response.ok || result.success === false) {
      const errorMessage = extractErrorMessage(result);
      throw new Error(errorMessage);
    }

    return result;
  } catch (error) {
    // Re-throw with proper error message
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while submitting your order');
  }
};

/**
 * Custom hook for handling order submission
 * @param {Function} onSuccess - Callback function called on successful submission
 * @returns {Object} Submission state and handler
 */
export const useOrderSubmission = (onSuccess) => {
  // Get data from Zustand store
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const cart = useFormStore((state) => state.cart);
  const scheduledAppointments = useFormStore((state) => state.scheduledAppointments);
  const userFormInformation = useFormStore((state) => state.userFormInformation);
  const selectedEstimateType = useFormStore((state) => state.selectedEstimateType);
  const needsPortableEquipment = useFormStore((state) => state.needsPortableEquipment);
  const shortLeadFormPreferredDate = useFormStore((state) => state.shortLeadFormPreferredDate);
  const shortLeadFormNote = useFormStore((state) => state.shortLeadFormNote);
  const selectedServices = useFormStore((state) => state.selectedServices);
  const setIsTransitioning = useFormStore((state) => state.setIsTransitioning);

  // Local state for submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  /**
   * Handle order submission
   */
  const handleSubmit = async () => {
    // Validate franchise info
    if (!currentFranchise?.id) {
      setSubmitError('Franchise information is missing');
      return;
    }

    setIsSubmitting(true);
    setIsTransitioning(true, 'Submitting your order...');
    setSubmitError(null);

    try {
      // Build payload from store data
      const payload = buildOrderPayload({
        cart,
        scheduledAppointments,
        userFormInformation,
        selectedEstimateType,
        needsPortableEquipment,
        currentFranchise,
        shortLeadFormPreferredDate,
        shortLeadFormNote,
        selectedServices
      });

      // Submit to API
      const result = await submitOrder(currentFranchise.id, payload);

      // Success - call the callback
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (error) {
      setSubmitError(error.message || 'An error occurred while submitting your order. Please try again.');
      setIsTransitioning(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Clear error message
   */
  const clearError = () => {
    setSubmitError(null);
  };

  return {
    isSubmitting,
    submitError,
    handleSubmit,
    clearError
  };
};
